import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { URL_PROXY } from './apiConfig';
import './Cancel.css';

const PHONE_DISPLAY = '+39 342 911 8790';
const PHONE_LINK = '+393429118790';
const EMAIL = 'info@taximoroder.it';

const Cancel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingId } = useParams();
  const token = new URLSearchParams(location.hash.replace(/^#/, '')).get('token') || '';
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const fetchBooking = useCallback(async () => {
    if (!bookingId) {
      setStatus('error');
      setError(t('cancel_invalid_link'));
      return;
    }
    if (!token) {
      setStatus('legacy');
      return;
    }
    try {
      const response = await axios.post(`${URL_PROXY}/booking/${bookingId}`, { token });
      setBooking(response.data.booking);
      setStatus(response.data.booking.status === 'CANCELLED' ? 'cancelled' : 'ready');
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('cancel_load_error'));
      setStatus('error');
    }
  }, [bookingId, token, t]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const confirmCancellation = async () => {
    setStatus('submitting');
    setError('');
    try {
      await axios.post(`${URL_PROXY}/cancelBooking/${bookingId}`, { token });
      setStatus('cancelled');
      setShowConfirmation(false);
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('cancel_submit_error'));
      setStatus('ready');
    }
  };

  if (status === 'loading') {
    return <main className="cancel-page"><section className="cancel-card cancel-message"><div className="cancel-spinner" /><p>{t('cancel_loading')}</p></section></main>;
  }

  if (status === 'error') {
    return <main className="cancel-page"><section className="cancel-card cancel-message"><WarningAmberRoundedIcon /><h1>{t('cancel_unavailable')}</h1><p>{error}</p><a href={`tel:${PHONE_LINK}`}><PhoneOutlinedIcon />{PHONE_DISPLAY}</a></section></main>;
  }

  if (status === 'legacy') {
    return (
      <main className="cancel-page">
        <section className="cancel-card cancel-message cancel-legacy">
          <WarningAmberRoundedIcon />
          <span className="cancel-legacy-kicker">{t('cancel_update_kicker')}</span>
          <h1>{t('cancel_legacy_title')}</h1>
          <p>{t('cancel_legacy_text')}</p>
          <div className="cancel-legacy-actions">
            <a href={`tel:${PHONE_LINK}`}><PhoneOutlinedIcon />{PHONE_DISPLAY}</a>
            <a href={`https://wa.me/${PHONE_LINK.replace('+', '')}`} target="_blank" rel="noopener noreferrer"><WhatsAppIcon />WhatsApp</a>
            <a href={`mailto:${EMAIL}`}><EmailOutlinedIcon />{EMAIL}</a>
          </div>
        </section>
      </main>
    );
  }

  if (status === 'cancelled') {
    return <main className="cancel-page"><section className="cancel-card cancel-message cancel-success"><div className="cancel-success-icon"><CheckRoundedIcon /></div><h1>{t('cancel_success_title')}</h1><p>{t('cancel_success_text')}</p><button type="button" onClick={() => navigate('/')}><HomeOutlinedIcon />{t('confirmation_home')}</button></section></main>;
  }

  return (
    <main className="cancel-page">
      <section className="cancel-card">
        <header className="cancel-topbar"><span>{t('cancel_kicker')}</span><span className="cancel-status"><i />{t('cancel_status')}</span></header>
        <div className="cancel-hero"><div className="cancel-warning"><WarningAmberRoundedIcon /></div><div><h1>{t('cancel_title')}</h1><p>{t('cancel_intro')}</p></div></div>

        <div className="cancel-layout">
          <section className="cancel-panel cancel-trip">
            <div className="cancel-section-heading"><span>01</span><div><h2>{t('cancel_ride_summary')}</h2><p>{t('cancel_summary_hint')}</p></div></div>
            <dl>
              <div><dt><PlaceOutlinedIcon />{t('cancel_start')}</dt><dd>{booking.origin}</dd></div>
              <div><dt><PlaceOutlinedIcon />{t('cancel_destination')}</dt><dd>{booking.destination}</dd></div>
              <div className="cancel-trip-meta"><span><dt><CalendarMonthOutlinedIcon />{t('booking_date')}</dt><dd>{booking.date}</dd></span><span><dt><AccessTimeOutlinedIcon />{t('booking_time')}</dt><dd>{booking.time}</dd></span><span><dt><PersonOutlineRoundedIcon />{t('booking_persons')}</dt><dd>{booking.persons}</dd></span></div>
            </dl>
            <div className="cancel-price"><span>{t('confirmation_price')}</span><strong>€ {Number(booking.price).toFixed(2)}</strong></div>
          </section>

          <aside className="cancel-panel cancel-action">
            <div className="cancel-section-heading"><span>02</span><div><h2>{t('cancel_action_title')}</h2><p>{t('cancel_action_hint')}</p></div></div>
            <div className="cancel-rule"><strong>{t('cancel_six_hours_title')}</strong><p>{t('cancel_six_hours_text')}</p></div>
            {error && <p className="cancel-error" role="alert">{error}</p>}
            {!showConfirmation ? (
              <button className="cancel-primary" type="button" onClick={() => setShowConfirmation(true)}>{t('cancel_cancel_booking')}</button>
            ) : (
              <div className="cancel-confirm-box"><strong>{t('cancel_confirm_title')}</strong><p>{t('cancel_confirm_text')}</p><div><button type="button" onClick={() => setShowConfirmation(false)}>{t('cancel_keep_booking')}</button><button className="danger" type="button" disabled={status === 'submitting'} onClick={confirmCancellation}>{status === 'submitting' ? t('cancel_submitting') : t('cancel_confirm_button')}</button></div></div>
            )}
            <a className="cancel-phone" href={`tel:${PHONE_LINK}`}><PhoneOutlinedIcon /><span><small>{t('cancel_call_instead')}</small>{PHONE_DISPLAY}</span></a>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Cancel;
